// HTML-in-Canvas API (Chrome 148–150 origin trial / chrome://flags/#canvas-draw-element).
//
// Model (mirrors the reference demo):
//   1. The element to capture lives as light-DOM child of a <canvas layoutsubtree>.
//      The browser lays it out using the canvas BITMAP size as the layout viewport.
//   2. `ctx.drawElementImage(el, 0, 0)` is called INSIDE `canvas.onpaint` — the
//      subtree is only laid out at paint time, so drawing outside onpaint yields
//      an empty bitmap.
//   3. `canvas.requestPaint()` schedules onpaint; `canvas.toBlob()` reads the pixels.
//
// Critical: the bitmap is sized to CSS pixels, NOT CSS × devicePixelRatio.
// Multiplying by DPR makes the subtree lay out at 2× and overflow the bitmap.

const DOWNLOAD_BUTTON_SELECTOR = '.promotional-banner__download';
const BANNER_SELECTOR = '.promotional-banner';

function waitForImages(root) {
  const imgs = root.querySelectorAll('img');
  return Promise.all(
    [...imgs].map((img) => {
      if (img.complete && img.naturalWidth > 0) return Promise.resolve();
      return new Promise((resolve) => {
        img.addEventListener('load', resolve, { once: true });
        img.addEventListener('error', resolve, { once: true });
      });
    })
  );
}

/**
 * Fetch a cross-origin image and return it as a base64 data: URL. A data: URL
 * is same-origin, so it never taints the canvas — sidestepping the CORS issue
 * that makes drawElementImage skip cross-origin (Shopify CDN) images. The fetch
 * itself works because the CDN sends Access-Control-Allow-Origin: *.
 * @param {string} url
 * @returns {Promise<string>}
 */
async function fetchAsDataURL(url) {
  const resp = await fetch(url, { mode: 'cors' });
  const blob = await resp.blob();
  return await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(/** @type {string} */ (reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

/**
 * Pick the largest width-descriptor candidate from an img's srcset, so the
 * export always captures at the highest resolution the theme serves — not the
 * (usually smaller) candidate the browser resolved for on-screen display.
 * @param {HTMLImageElement | undefined} img
 * @returns {string | null}
 */
function largestSrcsetCandidate(img) {
  const srcset = img?.getAttribute('srcset');
  if (!srcset) return null;
  let bestUrl = null;
  let bestWidth = 0;
  for (const part of srcset.split(',')) {
    const [url, descriptor] = part.trim().split(/\s+/);
    const width = descriptor?.endsWith('w') ? parseFloat(descriptor) : 0;
    if (url && width > bestWidth) {
      bestWidth = width;
      bestUrl = url;
    }
  }
  return bestUrl;
}

/**
 * Fetch an image and return it as a data: URL, pre-resampled to targetWidth
 * with high-quality filtering. drawElementImage downscales images with plain
 * bilinear filtering — drawing the full-res source straight into a much
 * smaller box comes out visibly softer than the DOM's resampler. Resizing
 * here with createImageBitmap({resizeQuality: 'high'}) to the exact pixel
 * size the image occupies in the export bitmap avoids that — in both
 * directions: a too-small source is upscaled here too, since a high-quality
 * resample still beats the paint-time bilinear stretch. PNG keeps the
 * intermediate lossless; the JPEG export at quality 1 is the only lossy
 * step.
 * @param {string} url
 * @param {number} targetWidth element box width x export scale (bitmap px)
 * @param {number} targetHeight element box height x export scale (bitmap px)
 * @param {string} [fitMode] the img's object-fit: 'contain' (default) or 'cover'
 * @returns {Promise<string>}
 */
async function fetchAsResizedDataURL(url, targetWidth, targetHeight, fitMode) {
  const resp = await fetch(url, { mode: 'cors' });
  const blob = await resp.blob();
  const probe = await createImageBitmap(blob);
  // The drawn size follows the img's object-fit. contain: the element box
  // fitted to the source's aspect ratio — a portrait packshot in a wide box
  // is height-limited, and resizing to the box WIDTH would leave a big
  // cheap-filter downscale at paint time anyway. cover: the source is
  // cropped, so the drawn (pre-crop) size is the box fitted the other way
  // around — min-fitting a wide photo into a short cover box would resample
  // it far below the box width and the paint would stretch it back up,
  // visibly blurry.
  let width;
  if (Number.isFinite(targetWidth) && Number.isFinite(targetHeight)) {
    const fit =
      fitMode === 'cover'
        ? Math.max(targetWidth / probe.width, targetHeight / probe.height)
        : Math.min(targetWidth / probe.width, targetHeight / probe.height);
    width = Math.max(1, Math.round(probe.width * fit));
  } else if (Number.isFinite(targetWidth)) {
    width = Math.max(1, Math.round(targetWidth));
  } else {
    width = probe.width;
  }
  let bitmap = probe;
  if (width !== probe.width) {
    if (width > probe.width) {
      // The CDN never upscales: a "3200w" candidate serves the original upload
      // width when the source file is smaller. Resampling up to the exact
      // target here beats the rasterizer's cheap bilinear stretch at paint
      // time, but real sharpness needs a larger upload.
      console.info(
        `[promotional-banner-download] bron is ${probe.width}px breed, export heeft ${width}px nodig — ` +
          'de afbeelding wordt opgeschaald. Upload een grotere afbeelding voor een scherper resultaat.'
      );
    }
    bitmap = await createImageBitmap(blob, { resizeWidth: width, resizeQuality: 'high' });
    probe.close();
  }
  const scratch = document.createElement('canvas');
  scratch.width = bitmap.width;
  scratch.height = bitmap.height;
  scratch.getContext('2d').drawImage(bitmap, 0, 0);
  bitmap.close();
  return scratch.toDataURL('image/png');
}

/**
 * Replace every <img> in the clone with an inlined base64 version of the
 * largest srcset candidate (falling back to the URL the original resolved),
 * pre-resampled to the pixel width it occupies in the export bitmap.
 * Drops <picture><source>/srcset/lazy first so the precise URL is what loads.
 * @param {Element} clone
 * @param {Element} original
 * @param {number} scale bitmap pixels per CSS pixel of the source banner
 */
async function inlineImages(clone, original, scale) {
  const originalImgs = [...original.querySelectorAll('img')];
  clone.querySelectorAll('picture source').forEach((/** @type {Element} */ s) => s.remove());
  const cloneImgs = /** @type {HTMLImageElement[]} */ ([...clone.querySelectorAll('img')]);
  await Promise.all(
    cloneImgs.map(async (img, i) => {
      const orig = /** @type {HTMLImageElement} */ (originalImgs[i]);
      const resolved = largestSrcsetCandidate(orig) || orig?.currentSrc || orig?.src || img.src;
      img.removeAttribute('srcset');
      img.removeAttribute('sizes');
      img.removeAttribute('loading');
      if (!resolved) return;
      try {
        const box = orig?.getBoundingClientRect();
        const targetWidth = box?.width > 0 ? box.width * scale : Infinity;
        const targetHeight = box?.height > 0 ? box.height * scale : Infinity;
        const fitMode = orig ? getComputedStyle(orig).objectFit : 'contain';
        img.src = await fetchAsResizedDataURL(resolved, targetWidth, targetHeight, fitMode);
        // Decode now so the pixels are ready before requestPaint(). Without this
        // the first (slow/uncached) capture works but cached re-runs paint before
        // the freshly-set data URL has decoded → blank images.
        await img.decode().catch(() => {});
      } catch (e) {
        /* leave original src; better a missing image than a failed capture */
      }
    })
  );
}

/**
 * Inline cross-origin CSS background images. This component drives all its
 * backgrounds off the `--pb-word-image` custom property (used by the word
 * text-clip and the layout-2/3/4 shapes), set inline on elements. Replacing the
 * property value with a base64 data: URL keeps the CSS rules that read
 * `var(--pb-word-image)` working, without tainting the canvas.
 * @param {Element} clone
 */
async function inlineCssBackgrounds(clone) {
  const urlRe = /url\(["']?([^"')]+)["']?\)/;
  const els = /** @type {HTMLElement[]} */ ([clone, ...clone.querySelectorAll('*')]);
  await Promise.all(
    els.map(async (el) => {
      if (!el.style) return;
      const value = el.style.getPropertyValue('--pb-word-image');
      const match = value ? value.match(urlRe) : null;
      const src = match?.[1];
      if (!src || src.startsWith('data:')) return;
      try {
        const dataUrl = await fetchAsDataURL(src);
        el.style.setProperty('--pb-word-image', `url('${dataUrl}')`);
        // Warm the decoder so the background is ready at paint time.
        await new Promise((resolve) => {
          const probe = new Image();
          probe.onload = probe.onerror = () => resolve(undefined);
          probe.src = dataUrl;
        });
      } catch (e) {
        /* leave original; decorative background only */
      }
    })
  );
}

// Desired export resolution multiplier. The banner is container-query based
// (cqw), so a larger bitmap scales the whole layout up proportionally — crisp
// output, no overflow. The canvas's CSS size stays at the banner's size
// (overlay match); only the bitmap is scaled, and toBlob() exports it.
const EXPORT_SCALE = 5;

// Hard cap on the exported image WIDTH (2× 1440 for high-DPI output). Also
// keeps the bitmap under the GPU's max texture size (commonly 4096 on
// integrated GPUs), which would otherwise crash the drawElementImage renderer.
const MAX_OUTPUT_WIDTH = 2880;

/**
 * @param {HTMLElement} banner
 * @returns {Promise<string>} base64 image/jpeg data: URL
 */
async function captureBannerToDataURL(banner) {
  const rect = banner.getBoundingClientRect();
  const cssWidth = Math.max(1, Math.round(rect.width));
  const cssHeight = Math.max(1, Math.round(rect.height));
  // A data-export-scale attribute pins the multiplier exactly (e.g. the
  // 620x410 newsletter card exports at 2x = 1240x820); otherwise clamp so
  // the bitmap WIDTH never exceeds MAX_OUTPUT_WIDTH.
  const scaleOverride = parseFloat(banner.dataset.exportScale || '');
  const scale =
    scaleOverride > 0
      ? scaleOverride
      : Math.max(1, Math.min(EXPORT_SCALE, MAX_OUTPUT_WIDTH / cssWidth));
  // Bitmap = CSS size × scale — this is the layout viewport for the subtree.
  const width = Math.round(cssWidth * scale);
  const height = Math.round(cssHeight * scale);

  const canvas = document.createElement('canvas');
  canvas.setAttribute('layoutsubtree', '');
  canvas.width = width;
  canvas.height = height;
  // Must be genuinely VISIBLE for the subtree to render (opacity:0 / z-index:-1
  // make Chrome skip the subtree, so drawElementImage captures nothing).
  // Overlay it exactly on the source banner → no visible flash → remove after.
  // CSS size stays at the banner's size; the bitmap is what gets scaled up.
  canvas.style.cssText = [
    'position: fixed',
    `left: ${rect.left}px`,
    `top: ${rect.top}px`,
    `width: ${cssWidth}px`,
    `height: ${cssHeight}px`,
    'margin: 0',
    'pointer-events: none',
    'z-index: 2147483647',
  ].join(';');

  // The captured element must be a child of the canvas (light-DOM fallback).
  const clone = /** @type {HTMLElement} */ (banner.cloneNode(true));
  clone.querySelectorAll(DOWNLOAD_BUTTON_SELECTOR).forEach((el) => el.remove());
  clone.style.width = '100%';
  clone.style.height = '100%';
  // Fixed-size cards (the newsletter formats) carry px min/max constraints
  // that would stop the clone from filling the scaled-up bitmap viewport.
  clone.style.minWidth = '0';
  clone.style.minHeight = '0';
  clone.style.maxWidth = 'none';
  clone.style.maxHeight = 'none';
  // The clone leaves its ancestors behind, so ancestor-scoped visibility
  // (e.g. newsletter cards are display: flex only under
  // [data-newsletter-filter]) would fall back to display: none and capture
  // as a blank bitmap. Pin the original's computed display inline.
  clone.style.display = getComputedStyle(banner).display;

  // Inline every <img> and CSS background as base64 data: URLs so the
  // cross-origin CDN images don't taint the canvas (which would make
  // drawElementImage skip them).
  await Promise.all([inlineImages(clone, banner, scale), inlineCssBackgrounds(clone)]);

  const ctx = canvas.getContext('2d');
  if (typeof ctx.drawElementImage !== 'function') {
    throw new Error(
      'drawElementImage unavailable — enable chrome://flags/#canvas-draw-element (Chrome 149+).'
    );
  }

  let resolvePainted;
  const painted = new Promise((resolve) => (resolvePainted = resolve));

  canvas.onpaint = () => {
    ctx.reset();
    // reset() also resets smoothing back to its 'low' default.
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    // JPG has no alpha; paint white first so transparent areas aren't black.
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawElementImage(clone, 0, 0);
    resolvePainted();
  };

  canvas.appendChild(clone);
  document.body.appendChild(canvas);

  try {
    if (document.fonts && document.fonts.ready) await document.fonts.ready;
    await waitForImages(clone);

    canvas.requestPaint();
    // Resolve when onpaint fired, with a timeout fallback, then give the
    // compositor one more frame to commit the bitmap before reading it.
    await Promise.race([painted, new Promise((r) => setTimeout(r, 3000))]);
    await new Promise((r) => requestAnimationFrame(r));

    // Return a base64 data: URL (not a blob). A data: URL is self-contained and
    // same-origin, so the <a download> works even inside the editor's sandboxed
    // iframe, where blob: downloads are blocked.
    return canvas.toDataURL('image/jpeg', 1);
  } finally {
    canvas.remove();
  }
}

/**
 * Convert a data: URL to a Blob. Blob URLs avoid Chrome's silent refusal of
 * very large data: URL downloads.
 * @param {string} dataUrl
 * @returns {Blob}
 */
function dataURLToBlob(dataUrl) {
  const comma = dataUrl.indexOf(',');
  const meta = dataUrl.slice(0, comma);
  const b64 = dataUrl.slice(comma + 1);
  const mime = meta.match(/:(.*?);/)?.[1] || 'image/jpeg';
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return new Blob([bytes], { type: mime });
}

/**
 * Download the image. Two defenses make this work inside the theme editor:
 *  - The anchor is placed inside a Shadow DOM host. The editor's delegated
 *    link-interceptor on document sees the click retargeted to the host (not an
 *    <a>), so it can't hijack/cancel it — while the anchor's default download
 *    action still fires inside the shadow tree.
 *  - A blob: URL (not a multi-MB data: URL) is used, which Chrome won't silently
 *    refuse to download.
 * @param {string} dataUrl
 * @param {string} filename
 */
function triggerDownload(dataUrl, filename) {
  const url = URL.createObjectURL(dataURLToBlob(dataUrl));

  const host = document.createElement('span');
  host.style.cssText = 'position:fixed;left:-9999px;top:0;width:0;height:0;';
  const root = host.attachShadow({ mode: 'open' });
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.rel = 'noopener';
  root.appendChild(a);
  document.body.appendChild(host);
  a.click();

  setTimeout(() => {
    host.remove();
    URL.revokeObjectURL(url);
  }, 1000);
}

/**
 * Open the image in a NEW top-level tab and auto-trigger the download there.
 * Inside the editor the preview iframe is sandboxed without allow-downloads, so
 * an in-frame download is impossible. A new tab escapes the iframe; if Shopify
 * set allow-popups-to-escape-sandbox the download fires automatically, otherwise
 * the image is shown full-size for a manual Cmd+S save. Returns false if popups
 * are blocked.
 * @param {string} dataUrl
 * @param {string} filename
 * @returns {boolean}
 */
function downloadViaNewTab(dataUrl, filename) {
  const win = window.open('', '_blank');
  if (!win) return false;
  const safe = String(filename).replace(/["\\<>]/g, '');
  win.document.write(
    '<!doctype html><meta charset="utf-8"><title>' + safe + '</title>' +
      '<body style="margin:0;min-height:100vh;display:grid;place-items:center;background:#111">' +
      '<img src="' + dataUrl + '" alt="" style="max-width:100%;max-height:100vh;display:block">' +
      '<a id="d" href="' + dataUrl + '" download="' + safe + '"></a>' +
      '<script>try{document.getElementById("d").click()}catch(e){}<\/script>' +
      '</body>'
  );
  win.document.close();
  return true;
}

function filenameFor(banner) {
  const title = banner.querySelector('.promotional-banner__title')?.textContent?.trim();
  const slug = (title || 'promotional-banner')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);
  return `${slug || 'promotional-banner'}.jpg`;
}

/** @returns {boolean} whether the HTML-in-Canvas API is available */
function supportsDrawElement() {
  try {
    const ctx = document.createElement('canvas').getContext('2d');
    return typeof (ctx && /** @type {any} */ (ctx).drawElementImage) === 'function';
  } catch (e) {
    return false;
  }
}

const FLAG_URL = 'chrome://flags/#canvas-draw-element';

/** Small toast telling the user to enable the required Chrome flag. */
function showFlagNotice() {
  const id = 'pb-flag-notice';
  if (document.getElementById(id)) return;

  const box = document.createElement('div');
  box.id = id;
  box.style.cssText =
    'position:fixed;bottom:16px;left:50%;transform:translateX(-50%);z-index:2147483647;' +
    'display:flex;align-items:center;gap:8px;max-width:90vw;padding:10px 14px;' +
    'background:#1f2430;color:#fff;border-radius:8px;box-shadow:0 6px 24px rgba(0,0,0,0.35);' +
    'font:13px/1.4 system-ui,-apple-system,sans-serif;';

  const text = document.createElement('span');
  text.textContent = 'Open url in tabblad en schakel in:';

  const url = document.createElement('span');
  url.textContent = FLAG_URL;
  // chrome:// URLs can't be navigated to from a page; user-select:all lets one
  // click select the whole URL so it's easy to copy.
  url.style.cssText = 'color:#6ea8ff;word-break:break-all;user-select:all;-webkit-user-select:all;';

  box.append(text, url);
  document.body.appendChild(box);
  setTimeout(() => box.remove(), 8000);
}

document.addEventListener('click', async (event) => {
  const button = event.target.closest(DOWNLOAD_BUTTON_SELECTOR);
  if (!button) return;
  const banner = button.closest(BANNER_SELECTOR);
  if (!banner) return;

  event.preventDefault();

  if (!supportsDrawElement()) {
    showFlagNotice();
    return;
  }

  if (button.dataset.busy === '1') return;
  button.dataset.busy = '1';
  button.setAttribute('aria-busy', 'true');

  try {
    const dataUrl = await captureBannerToDataURL(banner);
    const filename = filenameFor(banner);
    // Top-level (live storefront): download straight away. Inside an iframe
    // (theme editor) downloads are sandbox-blocked, so open the image in a new
    // top-level tab instead.
    if (window.self === window.top) {
      triggerDownload(dataUrl, filename);
    } else {
      downloadViaNewTab(dataUrl, filename);
    }
  } catch (err) {
    console.error('[promotional-banner-download]', err);
  } finally {
    delete button.dataset.busy;
    button.removeAttribute('aria-busy');
  }
});
